import {ResolvedVisualComponent} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/ResolvedVisualComponent';
import {PropBinding} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/binding/Binding';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {IHostContext} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/HostContext';
import {EditorPartState} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/EditorPartState';
import {PropConfig} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/PropConfig';
import {PropPathTree} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/PropPathTree';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {SystemPropsConfigs} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/SystemPropsConfigs';
import {EditorConfig} from '@mintdata/mint-kit/api/decorators/Editor';
import {BindingId, PropId} from '@mintdata/mint-kit/api/models/Identifiers';
import {BindingFormatBehavior} from '@mintdata/mint-kit/api/properties/BindingConfig';
import {IPropertyConverter} from '@mintdata/mint-kit/api/properties/PropertyConverter';
import {EditorId} from '@mintdata/mint-kit/api/properties/PropertyEditor';
import {PropertyTypeConstructor, PropName} from '@mintdata/mint-kit/api/properties/PropertySpec';
import {PropertyEditorConstructor} from '@mintdata/mint-kit/api/properties/PropertyTypeConfig';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {CommonUtils} from '@mintdata/mint-kit/impl/util/CommonUtils';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {
    deserialize,
    deserializeDiff,
    serialize,
    serializeDiff,
    SKIP
} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import {action, computed, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export enum PropertyGroup
{
    Base = 'baseProperty',
    Component = 'componentProperty',
    Layout = 'layoutProperty'
}

export interface IPropSnapshot
{
    id:PropId;
    name:PropName;
    group:PropertyGroup;
    bindingIds:Array<BindingId>;
    _value:any;
}

export interface IProperty
{
    id:PropId;

    box:Box;

    name:PropName;

    bindingIds:Array<BindingId>;

    addBinding(bindingId:BindingId);

    removeBinding(bindingId:BindingId):boolean;

    clearBindings();
}

export class Prop<T> implements IProperty
{
    public static HOST_CONTEXT:IHostContext = null;

    public id:PropId;

    public box:Box;

    public name:PropName;

    public group:PropertyGroup;

    public propConfig:PropConfig<T>;

    @observable
    public bindingIds:Array<BindingId>;

    @observable
    private _value:T | null;

    private _convertersTree:PropPathTree;

    private _editorStatesTree:PropPathTree;

    private constructor()
    {
        this._convertersTree = new PropPathTree(null, null);
        this._editorStatesTree = new PropPathTree(null, null);
    }

    public static createOf<T>(box:Box,
                              group:PropertyGroup,
                              propConfig:PropConfig<T>):Prop<T>
    {
        const prop:Prop<any> = new Prop();

        prop.id = IdUtil.newGUID();
        prop.box = box;
        prop.group = group;
        prop.propConfig = propConfig;

        prop.bindingIds = [];

        prop.name = propConfig.name;
        prop._value = propConfig.defaultValue
            ? deserialize(propConfig.typeCtor, serialize(propConfig.defaultValue))
            : AEmpty.Value;

        return prop;
    }

    public get converter():IPropertyConverter<any>
    {
        return this.getPropConverter(PropPath.ROOT);
    }

    public get title():string
    {
        return this.propConfig.title;
    }

    public get editor():PropertyEditorConstructor<any>
    {
        return this.propConfig.editor;
    }

    public get editorConfig():EditorConfig
    {
        return this.propConfig.editorConfig;
    }

    public get editorId():EditorId
    {
        if( !this.box )
            return this.name;

        return `${this.box.component.name}-${this.name}`;
    }

    public get isReadonly():boolean
    {
        return this.propConfig.isReadonly;
    }

    public get isRequired():boolean
    {
        return this.propConfig.isRequired;
    }

    public get typeCtor():any | null
    {
        return this.propConfig.typeCtor;
    }

    public get isArray():boolean
    {
        return this.propConfig.isArray;
    }

    @computed
    public get bindingsTree():PropPathTree
    {
        const tree = new PropPathTree(null, null);
        for( const bindingId of this.bindingIds )
        {
            const binding:PropBinding = Prop.HOST_CONTEXT.getPropBinding(bindingId);
            if( !binding ) continue;

            // Assert.yes(binding);

            tree.setValue(binding.propPath, binding);
        }

        return tree;
    }

    @computed
    public get boundValues():PropPathTree
    {
        if( !this.box )
            return this._getBoundValues(Repeater.initial());
        else
            return this._getBoundValues(Repeater.default(this.box.componentDesc));
    }

    @computed
    public get value():T
    {
        if( this.propConfig.isDisabled )
            return null;

        if( this.bindingIds.length === 0 )
            return this.innerValue;

        return this.boundValues.overrideClone(this.innerValue);
    }

    public cloneValue():any
    {
        return deserialize(this.propConfig.typeCtor, serialize(this.value));
    }

    public get innerValue():T
    {
        return this._value;
    }

    public cloneInnerValue():T
    {
        return deserialize(this.propConfig.typeCtor, serialize(this._value)) as T;
    }

    @computed
    public get pendingValue():T
    {
        if( this.propConfig.isDisabled )
            return null;

        return this._editorStatesTree.overrideClone(
            this.innerValue,
            (state) => state.pendingValue
        );
    }

    public getBinding(propPath:PropPath):PropBinding | null
    {
        return this.bindingsTree.getValue(propPath);
    }

    public getBindingId(propPath:PropPath):BindingId | null
    {
        const binding = this.getBinding(propPath);
        return binding && binding.id;
    }

    public canBindPath(propPath:PropPath):boolean
    {
        // TODO[ranges]: introduce some new mechanics instead of bindGroups ?
        //               or can we reuse current bindGroups information ?

        const propConfig = this.getPropertyConfig(propPath);
        return propConfig && propConfig.boundable;
    }

    public bindingFormatBehavior(propPath:PropPath):BindingFormatBehavior
    {
        const propConfig = this.getPropertyConfig(propPath);
        return propConfig ? propConfig.spec.binding.formatBehavior : BindingFormatBehavior.None;
    }

    public isBoundPath(propPath:PropPath):boolean
    {
        return !!this.getBinding(propPath);
    }

    public getBoundValues(repeater:Repeater):PropPathTree
    {
        return this._getBoundValues(repeater);
    }

    public getRepeatedValue(repeater:Repeater, boundValuesTree:PropPathTree = null):any
    {
        if( this.propConfig.isDisabled )
            return null;

        if( this.bindingIds.length === 0 )
            return this.innerValue;

        if( !boundValuesTree )
            boundValuesTree = this._getBoundValues(repeater);

        return boundValuesTree.overrideClone(this.innerValue);
    }

    public getRepeatedValueByPath(repeater:Repeater, propPath:PropPath):any
    {
        if( this.propConfig.isDisabled )
            return null;

        if( this.bindingIds.length === 0 )
            return this.getPropValue(propPath);

        const boundValuesTree:PropPathTree = this._getBoundValues(repeater);

        const value = boundValuesTree.getValue(propPath);

        return value || this.getPropValue(propPath);
    }

    public getPropValue<FT>(propPath:PropPath):FT
    {
        return propPath.getIn(this.value) as any as FT;
    }

    public getPropertyConfig<FT>(propPath:PropPath):PropConfig<FT>
    {
        return PropConfig.getSubPropConfig(this.propConfig, propPath);
    }

    public getPropType<FT>(propPath:PropPath):Type
    {
        const propConfig = this.getPropertyConfig(propPath);
        return propConfig && propConfig.type;
    }

    public getPropCtor<FT>(propPath:PropPath):PropertyTypeConstructor<FT>
    {
        const propConfig = this.getPropertyConfig<FT>(propPath);
        return propConfig ? propConfig.typeCtor : null;
    }

    public getIsArray(propPath:PropPath):boolean
    {
        const propConfig = this.getPropertyConfig(propPath);
        return propConfig && propConfig.isArray;
    }

    public getIsComposite(propPath:PropPath):boolean
    {
        const propConfig = this.getPropertyConfig(propPath);
        return propConfig && propConfig.isComposite;
    }

    public getArrayItemsCount<FT>(propPath:PropPath):number
    {
        const propValue:FT = this.getPropValue(propPath);

        if( propValue && (propValue as any).length != null )
            return (propValue as any).length;

        return 0;
    }

    public getPropConverter<FT>(propPath:PropPath):IPropertyConverter<FT>
    {
        let propConverter = this._convertersTree.getValue(propPath);
        if( !propConverter )
        {
            const propConfig = this.getPropertyConfig(propPath);

            propConverter = propConfig && propConfig.createConverter();

            this._convertersTree.setValue(propPath, propConverter);
        }

        return propConverter;
    }

    public getEditorPartState<FT>(propPath:PropPath):EditorPartState<FT>
    {
        let propState = this._editorStatesTree.getValue(propPath);
        if( !propState )
        {
            propState = new EditorPartState();
            this._editorStatesTree.setValue(propPath, propState);
        }

        return propState;
    }

    public clone(box:Box):Prop<T>
    {
        const prop:Prop<T> = new Prop();

        prop.id = IdUtil.newGUID();
        prop.name = this.name;
        prop._value = this.cloneInnerValue();
        prop.group = this.group;
        prop.propConfig = this.propConfig;

        prop.box = box;

        // NOTE: bindings will be replaced in `SurfaceAreaController.cloneBox` method,
        //  which ultimately is the the only one calling this `Prop.clone`
        prop.bindingIds = [...this.bindingIds];

        return prop;
    }

    @action.bound
    public setInnerValue(value:any):void
    {
        this._value = value;
    }

    @action.bound
    public addBinding(bindingId:BindingId)
    {
        this.bindingIds.push(bindingId);
    }

    @action.bound
    public removeBinding(bindingId:BindingId):boolean
    {
        const index = this.bindingIds.findIndex(id => id === bindingId);
        if( index < 0 )
            return false;

        this.bindingIds.splice(index, 1);

        return true;
    }

    @action.bound
    public clearBindings():void
    {
        this.bindingIds = [];
    }

    @action.bound
    public setPropValue<FT>(propPath:PropPath, value:FT):void
    {
        if( propPath.isRoot() )
        {
            this.setInnerValue(value as FT);
            return;
        }

        // we need fresh instance to hint mobx, that prop's value did actually change
        const rootValue = CommonUtils.shallowClone(this.innerValue);

        // now, basically, navigate by propPath in both propValue and propConfig trees
        let propValue = rootValue;
        let propConfig = this.propConfig;
        let isArray = propConfig.isArray;

        const pathParts = propPath.parts;
        const pathLength = pathParts.length;
        for( let i = 0; i < pathLength - 1; i++ )
        {
            const pathPart = pathParts[i];

            let subConfig;
            if( isNaN(pathPart as any) )
            {
                subConfig = propConfig.propertiesConfigs.get(pathPart as string);
                Assert.yes(subConfig, 'sub-config should exist on that point');
            }
            else
            {
                // moving to array's element => propConfig stays the same, but isArray=false (? can we support Array of Arrays ?)
                subConfig = propConfig;
                isArray = false;
            }

            let subValue = CommonUtils.shallowClone(propValue[pathPart]);
            propValue[pathPart] = subValue;

            if( subValue == null )
            {
                // create default value if missing
                subValue = propValue[pathPart] = isArray ? [] : subConfig.createDefaultValue();

                // skip setting a value, if we don't have anywhere to set
                if( subValue == null ) return;
            }

            propValue = subValue;
            propConfig = subConfig;
        }

        const oldValue = propValue[pathParts[pathLength - 1]];
        propValue[pathParts[pathLength - 1]] = value;

        this.setInnerValue(rootValue);

        Prop.HOST_CONTEXT.onPropValueChanged(this, propPath, value, oldValue);
    }

    @action.bound
    public addArrayItem<FT>(propPath:PropPath, index:number, value:FT):void
    {
        const arrayValue = propPath.getIn(this.innerValue) as Array<any>;
        Assert.yes(arrayValue, 'arrayProperty should not be null to be able to add new item');

        if( value == null )
            value = this.getPropertyConfig<FT>(propPath).createDefaultValue();

        // just push to the end ...
        const newArrayValue = [...arrayValue, value];
        this.setPropValue(propPath, newArrayValue);

        // .. and now move separately, if needed (to also recalculate bindings properly)
        if( index != null )
            this.moveArrayItem(propPath, newArrayValue.length, index);
    }

    @action.bound
    public removeArrayItem<FT>(propPath:PropPath, index:number):any
    {
        Prop.HOST_CONTEXT.onPropArrayItemRemoved(this, propPath, index);

        const arrayValue = propPath.getIn(this.innerValue) as Array<any>;

        const newArrayValue = [
            ...arrayValue.slice(0, index),
            ...arrayValue.slice(index + 1)
        ];

        this.setPropValue(propPath, newArrayValue);

        return arrayValue[index];
    }

    @action.bound
    public removeArrayItems<FT>(propPath:PropPath):void
    {
        this.setPropValue(propPath, []);
    }

    @action.bound
    public moveArrayItem<FT>(propPath:PropPath, from:number, to:number):void
    {
        const arrayValue = propPath.getIn(this.innerValue) as Array<any>;
        Assert.yes(arrayValue, 'arrayProperty should not be null to be able to move an item');

        if( from < 0 || from >= arrayValue.length ||
            to < 0 || to >= arrayValue.length ||
            from === to )
        {
            return;
        }

        Prop.HOST_CONTEXT.onPropArrayItemMoved(this, propPath, from, to);

        const newArrayValue = [
            ...arrayValue.slice(0, from),
            ...arrayValue.slice(from + 1)
        ];
        newArrayValue.splice(to, 0, arrayValue[from]);

        this.setPropValue(propPath, newArrayValue);
    }

    private _getBoundValues(repeater:Repeater):PropPathTree
    {
        return this.bindingsTree.transformWithShadowing(binding =>
            Prop.HOST_CONTEXT.getPropBoundValue<T>(this, binding, repeater)
        );
    }

    public static getSurfacePropNames():Array<string>
    {
        return [
            SystemPropsConfigs.VIEWPORT_LAYOUT_PROP_NAME,
            SystemPropsConfigs.SCROLLBAR_PROP_NAME
        ];
    }

    public static createSurfaceProps():Map<PropId, Prop<any>>
    {
        const props:Map<PropId, Prop<any>> = new Map();
        this.getSurfacePropNames().forEach(propName =>
        {
            const prop = this.createSurfaceProp(propName);
            props.set(prop.id, prop);
        });

        return props;
    }

    public static createSurfaceProp<T>(basePropName:string):Prop<T>
    {
        return Prop.createOf<T>(
            null,
            PropertyGroup.Base,
            SystemPropsConfigs.basePropertiesConfigs.get(basePropName)
        );
    }

    public static toJson:ITransformer<Prop<any>, IPropSnapshot> = createTransformer((prop:Prop<any>) =>
    {
        const valueJson:any = prop._value ? serializeDiff(prop._value, prop.propConfig.defaultValue) : null;

        return prop.bindingIds.length > 0 || (valueJson != null && valueJson !== SKIP)
            ? {
                id: prop.id,
                name: prop.name,
                group: prop.group,
                bindingIds: [...prop.bindingIds],
                _value: valueJson
            }
            : null;
    });

    public static fromJson(json:IPropSnapshot,
                           box:Box,
                           currentState?:Prop<any>,
                           currentJson?:IPropSnapshot):Prop<any>
    {
        if( !currentState )
            return Prop.fromJsonDeep(json, box);

        if( !currentJson )
            currentJson = Prop.toJson(currentState);

        const prop:Prop<any> = new Prop();

        prop.id = json.id;
        prop.name = json.name;
        prop.group = json.group as PropertyGroup;
        prop.bindingIds = json.bindingIds ? json.bindingIds.filter(bindingId => !!bindingId) : [];

        const resolvedVisualComponent:ResolvedVisualComponent = box.component.ref.resolvedComponent;
        let propConfig:PropConfig<any>;

        switch( prop.group )
        {
            case PropertyGroup.Base:
                propConfig = SystemPropsConfigs.basePropertiesConfigs.get(prop.name);
                break;
            case PropertyGroup.Layout:
                propConfig = resolvedVisualComponent.layoutPropertiesConfigs.get(prop.name);
                break;
            default:
                propConfig = resolvedVisualComponent.propertiesConfigs.get(prop.name);
        }

        prop.propConfig = propConfig;
        prop.box = box;

        prop._value = json && currentJson && json._value === currentJson._value
            ? currentState._value
            : deserializeDiff(propConfig.typeCtor, json._value, propConfig.defaultValue);

        return prop;
    }

    public static fromJsonDeep(json:IPropSnapshot, box:Box):Prop<any>
    {
        const prop:Prop<any> = new Prop();

        prop.id = json.id;
        prop.name = json.name;
        prop.group = json.group as PropertyGroup;
        prop.bindingIds = json.bindingIds ? json.bindingIds.filter(bindingId => !!bindingId) : [];

        let resolvedVisualComponent:ResolvedVisualComponent;
        if( box )
            resolvedVisualComponent = box.component.ref.resolvedComponent;

        let propConfig:PropConfig<any>;

        switch( prop.group )
        {
            case PropertyGroup.Base:
                propConfig = SystemPropsConfigs.basePropertiesConfigs.get(prop.name);
                break;
            case PropertyGroup.Layout:
                propConfig = resolvedVisualComponent.layoutPropertiesConfigs.get(prop.name);
                break;
            default:
                propConfig = resolvedVisualComponent.propertiesConfigs.get(prop.name);
        }

        prop._value = deserializeDiff(propConfig.typeCtor, json._value, propConfig.defaultValue);
        prop.propConfig = propConfig;
        prop.box = box;

        return prop;
    }
}
